import React, { useMemo } from 'react';
import { CaretDownIcon, LightningIcon, LockIcon } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { UnifiedForecastResult } from '@erp/shared-types';
import type {
  SupplierPayment,
  RebateAgreementWithStructure,
} from '@/api/rebates/types';
import { formatCurrency } from '@erp/shared-utils';
import { ReconciliationBreakdownTable } from './ReconciliationBreakdownTable';
import useReopenRebateAgreement from '@/api/rebates/useReopenRebateAgreement';

interface RebateReconciliationSectionProps {
  reconciliationRef: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onToggle: (open: boolean) => void;
  forecastData?: UnifiedForecastResult;
  supplierPayments?: SupplierPayment[];
  loading?: boolean;
  agreement?: RebateAgreementWithStructure;
  onCloseAgreement?: () => void;
  onReopenAgreement?: () => void;
  rules?: Array<{ id: string; name: string }>;
  divisions?: Array<{ id: string; name: string }>;
}

export function RebateReconciliationSection({
  reconciliationRef,
  isExpanded,
  onToggle,
  forecastData,
  supplierPayments,
  loading = false,
  agreement,
  onCloseAgreement,
  onReopenAgreement,
  rules = [],
  divisions = [],
}: RebateReconciliationSectionProps) {
  const reopenMutation = useReopenRebateAgreement(agreement?.id || '');

  // Calculate summary totals
  const summary = useMemo(() => {
    if (!forecastData?.estimates || !supplierPayments) {
      return { expected: 0, allocated: 0, variance: 0 };
    }

    // Aggregate forecast estimates
    let expected = 0;
    forecastData.estimates.forEach((estimate) => {
      estimate.breakdowns.forEach((breakdown) => {
        expected += breakdown.rebateAmount;
      });
    });

    // Aggregate payment allocations
    let allocated = 0;
    supplierPayments.forEach((payment) => {
      if (payment.allocations) {
        payment.allocations.forEach((allocation) => {
          allocated += allocation.allocatedAmount;
        });
      }
    });

    const variance = allocated - expected;

    return { expected, allocated, variance };
  }, [forecastData, supplierPayments]);

  // Determine if agreement can be closed
  const canClose = useMemo(() => {
    if (!agreement) return false;
    if (agreement.status !== 'finalized') return false;
    if (agreement.isClosedOut) return false;
    return true;
  }, [agreement]);

  // Determine if agreement can be reopened
  const canReopen = useMemo(() => {
    if (!agreement) return false;
    return agreement.isClosedOut === true;
  }, [agreement]);

  const handleReopen = () => {
    if (!agreement?.id) return;
    reopenMutation.mutate(undefined, {
      onSuccess: () => {
        if (onReopenAgreement) {
          onReopenAgreement();
        }
      },
    });
  };

  return (
    <div ref={reconciliationRef}>
      <Card
        className={agreement?.isClosedOut ? 'bg-slate-50 border-slate-300' : ''}
      >
        <Collapsible open={isExpanded} onOpenChange={onToggle}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {agreement?.isClosedOut ? (
                    <LockIcon className="h-5 w-5 text-slate-500" />
                  ) : (
                    <LightningIcon className="h-5 w-5 text-primary" />
                  )}
                  <h2 className="text-lg font-semibold">
                    Reconciliation Dashboard
                    {agreement?.isClosedOut && (
                      <Badge variant="outline" className="ml-2 bg-slate-100">
                        Closed
                      </Badge>
                    )}
                  </h2>
                </div>
                <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {agreement?.isClosedOut && agreement.closureMetadata && (
                <div className="mb-4 p-3 bg-slate-100 border border-slate-300 rounded-md">
                  <div className="text-sm text-slate-700">
                    <div className="font-medium mb-1">Agreement Closed</div>
                    <div className="text-xs space-y-1">
                      <div>
                        Closed:{' '}
                        {new Date(
                          agreement.closureMetadata.closedAt,
                        ).toLocaleDateString()}{' '}
                        by {agreement.closureMetadata.closedByUserName}
                      </div>
                      <div>Reason: {agreement.closureMetadata.reason}</div>
                      {agreement.closureMetadata.snapshotData.writeOffAmount !==
                        0 && (
                        <div>
                          Write-off:{' '}
                          {formatCurrency(
                            Math.abs(
                              agreement.closureMetadata.snapshotData
                                .writeOffAmount,
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {loading ? (
                <div className="text-center p-6 text-muted-foreground">
                  Loading reconciliation data...
                </div>
              ) : !forecastData || !supplierPayments ? (
                <div className="text-center p-6">
                  <LightningIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <h3 className="text-lg font-medium text-muted-foreground">
                    No Reconciliation Data
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Reconciliation data will appear once forecasts are generated
                    and payments are allocated
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Card */}
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">
                            Expected Rebate
                          </div>
                          <div className="text-lg font-semibold">
                            {formatCurrency(summary.expected)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">
                            Total Allocated
                          </div>
                          <div className="text-lg font-semibold">
                            {formatCurrency(summary.allocated)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">
                            Difference
                          </div>
                          <div
                            className={`text-lg font-semibold ${
                              summary.variance > 0
                                ? 'text-purple-600'
                                : summary.variance < 0
                                  ? 'text-red-600'
                                  : 'text-gray-600'
                            }`}
                          >
                            {formatCurrency(Math.abs(summary.variance))}
                          </div>
                        </div>
                        <div className="flex items-end">
                          {canReopen && onReopenAgreement ? (
                            <Button
                              onClick={handleReopen}
                              className="w-full"
                              variant="outline"
                              disabled={reopenMutation.isPending}
                            >
                              {reopenMutation.isPending
                                ? 'Reopening...'
                                : 'Reopen Agreement'}
                            </Button>
                          ) : canClose && onCloseAgreement ? (
                            <Button
                              onClick={onCloseAgreement}
                              className="w-full"
                              variant="default"
                            >
                              Close Agreement & Write Off{' '}
                              {formatCurrency(Math.abs(summary.variance))}
                            </Button>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              {!agreement
                                ? 'No agreement data'
                                : agreement.status !== 'finalized'
                                  ? 'Agreement must be finalized'
                                  : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Reconciliation Breakdown Table */}
                  {rules.length > 0 && divisions.length > 0 && (
                    <ReconciliationBreakdownTable
                      forecastData={forecastData}
                      supplierPayments={supplierPayments}
                      rules={rules}
                      divisions={divisions}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
}
